import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState, Stat } from "./_shared";
import { StatusBadge } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { sessionTopics } from "@/components/toolbox/TopicContent";
import { CreateTalkDialog } from "@/components/toolbox/CreateTalkDialog";
import { TopicDialog } from "@/components/toolbox/TopicDialog";
import {
  Plus, Search, Users, PenLine, ClipboardList, Library, LayoutDashboard,
  AlertTriangle, CalendarClock, ChevronRight, ClipboardCheck, Eye, EyeOff, Paperclip,
} from "lucide-react";
import { toast } from "sonner";

const DELIVER = ["admin", "office", "super_admin", "foreman", "quantity_surveyor", "field"];
const OFFICE = ["admin", "office", "super_admin"];
const STATUS_FILTERS = ["all", "draft", "scheduled", "in_progress", "completed"];

export default function ToolboxTalks() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canDeliver = DELIVER.includes(user?.role);
  const isOffice = OFFICE.includes(user?.role);
  const isField = user?.role === "field";

  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [talks, setTalks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [libCat, setLibCat] = useState("ALL");
  const [libQ, setLibQ] = useState("");

  const [params] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  useEffect(() => { if (canDeliver && params.get("new") === "1") setCreateOpen(true); }, [params, canDeliver]);
  const [topicDialog, setTopicDialog] = useState({ open: false, topic: null });

  const loadTalks = async () => {
    const { data } = await api.get("/tbx/talks");
    setTalks(data.talks || []);
  };
  const loadStats = async () => {
    const { data } = await api.get("/tbx/stats");
    setStats(data);
  };
  const loadTopics = async () => {
    const { data } = await api.get("/tbx/topics", { params: { category: "ALL" } });
    setTopics(data.topics || []);
    setCategories(data.categories || []);
  };

  const toggleHide = async (t) => {
    try {
      await api.post(`/tbx/topics/${t.id}/${t.hidden_for_crew ? "unhide" : "hide"}`);
      toast.success(t.hidden_for_crew ? "Topic shown to crew" : "Topic hidden from crew");
      loadTopics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };

  const bulkHide = async (category, hidden) => {
    try {
      const { data } = await api.post("/tbx/topics/hide-category", { category, hidden });
      toast.success(`${hidden ? "Hid" : "Restored"} ${data.count} "${category}" topic${data.count === 1 ? "" : "s"} ${hidden ? "from" : "to"} crew`);
      loadTopics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update category"); }
  };

  useEffect(() => {
    loadStats().catch(() => {});
    loadTalks().catch(() => {});
    loadTopics().catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const shownTalks = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return talks.filter((t) =>
      (statusFilter === "all" || t.status === statusFilter) &&
      (!ql || [t.topic_title_snapshot, ...sessionTopics(t).map(topic => topic.title_snapshot), t.job_title, t.job_number, t.delivered_by_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)))
    );
  }, [talks, statusFilter, q]);

  const shownTopics = useMemo(() => {
    const ql = libQ.trim().toLowerCase();
    return topics.filter((t) =>
      (libCat === "ALL" || t.category === libCat) &&
      (!ql || [t.title, t.category, t.short_description].filter(Boolean).some((v) => v.toLowerCase().includes(ql)))
    );
  }, [topics, libCat, libQ]);

  const recent = talks.slice(0, 5);

  const newTalkBtn = canDeliver && (
    <Button onClick={() => setCreateOpen(true)}
      className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
      data-testid="new-talk-button">
      <Plus className="w-4 h-4 mr-2" /> New talk
    </Button>
  );

  const TalkRow = ({ t }) => (
    <button onClick={() => nav(`/app/toolbox/${t.id}`)}
      className="w-full text-left hard-card p-4 flex flex-wrap sm:flex-nowrap items-center gap-3 hover:border-[#FF5F15]/60 transition-colors group"
      data-testid={`talk-row-${t.id}`}>
      <div className="w-10 h-10 rounded-lg bg-[#FF5F15]/10 flex items-center justify-center shrink-0">
        <ClipboardCheck className="w-5 h-5 text-[#FF5F15]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-zinc-900 dark:text-white truncate group-hover:text-[#FF5F15]">{t.topic_title_snapshot}</div>
        <div className="text-[10px] font-bold uppercase text-[#FF5F15] mt-1">{sessionTopics(t).length} topic{sessionTopics(t).length === 1 ? "" : "s"}</div>
        {sessionTopics(t).length > 1 && <div className="text-xs text-zinc-500">{sessionTopics(t).slice(0, 2).map((topic, i) => <p key={i} className="truncate">{topic.title_snapshot}</p>)}{sessionTopics(t).length > 2 && <p>+{sessionTopics(t).length - 2} more</p>}</div>}
        <div className="text-xs text-zinc-500 truncate">{t.job_number} — {t.job_title} · {t.talk_date}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-auto">
        {t.needs_my_signon && (
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] uppercase tracking-wider font-bold" data-testid={`talk-needs-signon-${t.id}`}>
            <PenLine className="w-3 h-3" /> Sign on
          </span>
        )}
        <span className="flex items-center gap-1 text-xs text-zinc-500" title="Signed / expected">
          <Users className="w-3.5 h-3.5" /> {t.signed_count}{t.expected_count ? `/${t.expected_count}` : ""}
        </span>
        <StatusBadge status={t.status} />
        <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#FF5F15]" />
      </div>
    </button>
  );

  if (isField) {
    const needsMe = talks.filter((t) => t.needs_my_signon);
    const others = talks.filter((t) => !t.needs_my_signon);
    return (
      <div data-testid="toolbox-page" className="px-4 py-4 pb-24">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold">// Safety</div>
          <h1 className="text-3xl font-display uppercase tracking-tight text-zinc-900 dark:text-white mt-1">Toolbox Talks</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Read the talk and sign on. Tap New talk to run one with your crew.</p>
        </div>

        <Button onClick={() => setCreateOpen(true)}
          className="w-full mb-5 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold h-12"
          data-testid="new-talk-button">
          <Plus className="w-4 h-4 mr-2" /> New talk
        </Button>

        {needsMe.length > 0 && (
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-600 dark:text-amber-400 font-bold mb-2 flex items-center gap-1.5">
              <PenLine className="w-3.5 h-3.5" /> Needs your sign-on
            </div>
            <div className="space-y-2.5">{needsMe.map((t) => <TalkRow key={t.id} t={t} />)}</div>
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">// {needsMe.length > 0 ? "Other talks" : "All talks"}</div>
        {talks.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No toolbox talks yet"
            description="Tap New talk to run your first pre-start safety talk and sign on."
            action={<Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="empty-new-talk"><Plus className="w-4 h-4 mr-2" /> New talk</Button>} />
        ) : others.length === 0 ? (
          <p className="text-sm text-zinc-500">You're all signed on. Nice one.</p>
        ) : (
          <div className="space-y-2.5">{others.map((t) => <TalkRow key={t.id} t={t} />)}</div>
        )}

        <CreateTalkDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          jobs={jobs}
          users={users}
          topics={topics}
          categories={categories}
          onCreated={(tid) => nav(`/app/toolbox/${tid}`)}
        />
      </div>
    );
  }

  return (
    <div data-testid="toolbox-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-7xl md:mx-auto pb-24 md:pb-10">
      <PageHeader
        tag="/ safety"
        title="Toolbox Talks"
        subtitle="Run pre-start safety talks, get the crew to sign on-screen, and auto-file a signed PDF to the job."
        action={newTalkBtn}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="talks" data-testid="tab-talks"><ClipboardList className="w-4 h-4 mr-1.5" /> Talks</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library"><Library className="w-4 h-4 mr-1.5" /> Topic library</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <Stat label="Talks (30d)" value={stats?.talks_30d ?? "—"} accent="bg-[#FF5F15]" />
            <Stat label="Sign-ons (30d)" value={stats?.signons_30d ?? "—"} accent="bg-blue-500" />
            <Stat label="Sign-on rate" value={stats ? `${stats.signon_rate}%` : "—"} accent="bg-green-500" />
            <Stat label="Overdue" value={stats?.overdue ?? "—"} accent="bg-red-500" />
          </div>

          {stats?.overdue > 0 && (
            <div className="hard-card p-4 flex items-center gap-3 border-l-4 border-l-red-500" data-testid="overdue-alert">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <div className="text-sm text-zinc-700 dark:text-zinc-200">
                <span className="font-bold">{stats.overdue}</span> scheduled talk{stats.overdue === 1 ? "" : "s"} {stats.overdue === 1 ? "is" : "are"} past their date and not completed.
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold">// Recent talks</div>
              {talks.length > 5 && (
                <button onClick={() => setTab("talks")} className="text-xs text-zinc-500 hover:text-[#FF5F15] flex items-center gap-1">
                  View all <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {recent.length === 0 ? (
              <EmptyState icon={CalendarClock} title="No toolbox talks yet"
                description="Kick off your first pre-start safety talk. The crew signs on-screen and a signed PDF is filed to the job automatically."
                action={newTalkBtn} />
            ) : (
              <div className="space-y-2.5">{recent.map((t) => <TalkRow key={t.id} t={t} />)}</div>
            )}
          </div>
        </TabsContent>

        {/* TALKS */}
        <TabsContent value="talks" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search talks, jobs, presenters…"
                className="pl-9 w-full" data-testid="talks-search" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {STATUS_FILTERS.map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold border transition-colors ${
                    statusFilter === s ? "bg-[#FF5F15] border-[#FF5F15] text-white"
                      : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-[#FF5F15]"}`}
                  data-testid={`talks-filter-${s}`}>
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          {shownTalks.length === 0 ? (
            <EmptyState icon={ClipboardList} title={talks.length === 0 ? "No talks yet" : "No matches"}
              description={talks.length === 0 ? "Create your first toolbox talk." : "Try a different search or filter."}
              action={newTalkBtn} />
          ) : (
            <div className="space-y-2.5">{shownTalks.map((t) => <TalkRow key={t.id} t={t} />)}</div>
          )}
        </TabsContent>

        {/* LIBRARY */}
        <TabsContent value="library" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input value={libQ} onChange={(e) => setLibQ(e.target.value)} placeholder="Search topics…"
                className="pl-9 w-full" data-testid="library-search" />
            </div>
            {isOffice && (
              <Button onClick={() => setTopicDialog({ open: true, topic: null })}
                variant="outline" className="rounded-sm uppercase text-xs font-bold tracking-wider" data-testid="new-topic-button">
                <Plus className="w-4 h-4 mr-1.5" /> New topic
              </Button>
            )}
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {["ALL", ...categories].map((c) => (
              <button key={c} onClick={() => setLibCat(c)}
                className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold border transition-colors ${
                  libCat === c ? "bg-[#FF5F15] border-[#FF5F15] text-white"
                    : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-[#FF5F15]"}`}
                data-testid={`library-cat-${c}`}>
                {c}
              </button>
            ))}
          </div>
          {isOffice && libCat !== "ALL" && (
            <div className="flex items-center gap-2 -mt-1" data-testid="bulk-hide-bar">
              <span className="text-[11px] text-zinc-500">Whole "{libCat}" category:</span>
              <button onClick={() => bulkHide(libCat, true)}
                className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 hover:text-[#FF5F15] inline-flex items-center gap-1"
                data-testid="bulk-hide-cat">
                <EyeOff className="w-3.5 h-3.5" /> Hide all from crew
              </button>
              <span className="text-zinc-300">·</span>
              <button onClick={() => bulkHide(libCat, false)}
                className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 hover:text-[#FF5F15] inline-flex items-center gap-1"
                data-testid="bulk-show-cat">
                <Eye className="w-3.5 h-3.5" /> Show all
              </button>
            </div>
          )}
          {shownTopics.length === 0 ? (
            <EmptyState icon={Library} title="No topics found" description="Try a different category or search." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {shownTopics.map((t) => (
                <div key={t.id}
                  className={`text-left hard-card p-4 hover:border-[#FF5F15]/60 transition-colors group flex flex-col ${t.hidden_for_crew ? "opacity-80" : ""}`}
                  data-testid={`library-topic-${t.id}`}>
                  <button type="button" onClick={() => setTopicDialog({ open: true, topic: t })} className="text-left flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{t.category}</Badge>
                      {t.is_system_template
                        ? <span className="text-[9px] uppercase tracking-wider text-zinc-400">Template</span>
                        : <span className="text-[9px] uppercase tracking-wider text-[#FF5F15]">Yours</span>}
                      {(t.attachments?.length > 0) && (
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500 inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" /> {t.attachments.length}</span>
                      )}
                      {t.hidden_for_crew && (
                        <span className="text-[9px] uppercase tracking-wider text-amber-600 inline-flex items-center gap-0.5" data-testid={`topic-hidden-badge-${t.id}`}><EyeOff className="w-3 h-3" /> Hidden from crew</span>
                      )}
                    </div>
                    <div className="font-bold text-sm text-zinc-900 dark:text-white group-hover:text-[#FF5F15]">{t.title}</div>
                    <div className="text-xs text-zinc-500 line-clamp-2 mt-1">{t.short_description}</div>
                  </button>
                  {isOffice && (
                    <div className="mt-3 pt-2 border-t border-zinc-100 dark:border-white/10 flex items-center gap-2">
                      <button type="button" onClick={() => toggleHide(t)}
                        className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 hover:text-[#FF5F15] inline-flex items-center gap-1"
                        data-testid={`topic-hide-toggle-${t.id}`}>
                        {t.hidden_for_crew ? <><Eye className="w-3.5 h-3.5" /> Show to crew</> : <><EyeOff className="w-3.5 h-3.5" /> Hide from crew</>}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <CreateTalkDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        jobs={jobs}
        users={users}
        topics={topics}
        categories={categories}
        onCreated={(tid) => nav(`/app/toolbox/${tid}`)}
      />
      <TopicDialog
        open={topicDialog.open}
        onOpenChange={(v) => setTopicDialog((s) => ({ ...s, open: v }))}
        topic={topicDialog.topic}
        categories={categories}
        isOffice={isOffice}
        onSaved={loadTopics}
      />
    </div>
  );
}
